import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Chat, ChatWithDetails } from '../types';
import { calculateUnreadCount } from '../utils/chatHelpers';

/**
 * Hook to fetch and manage user's chats
 */
export const useChats = (userId?: string) => {
  const [chats, setChats] = useState<ChatWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChats = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      // First, get chat IDs where user is participant and has not left
      const { data: participantData } = await supabase
        .from('chat_participants')
        .select('chat_id')
        .eq('user_id', userId)
        .is('left_at', null);

      const chatIds = participantData?.map((p) => p.chat_id) || [];

      if (chatIds.length === 0) {
        setChats([]);
        setLoading(false);
        return;
      }

      // Fetch chats with participants
      const { data, error: fetchError } = await supabase
        .from('chats')
        .select(`
          *,
          participants:chat_participants(*)
        `)
        .in('id', chatIds)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (fetchError) throw fetchError;

      // Get all unique user IDs from participants
      const allUserIds = new Set<string>();
      (data || []).forEach((chat: any) => {
        chat.participants?.forEach((p: any) => {
          if (p.user_id) allUserIds.add(p.user_id);
        });
      });

      // Fetch all profiles in one query (only if we have user IDs)
      let profiles: any[] = [];
      if (allUserIds.size > 0) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, nickname, avatar_url, email, is_dependent')
          .in('id', Array.from(allUserIds));
        
        if (profileError) {
          console.error('Error fetching profiles:', profileError);
        }
        profiles = profileData || [];
      }

      // Create a map for quick profile lookup
      const profileMap = new Map<string, any>(
        profiles.map((p: any) => [p.id, p])
      );

      // Fetch last message for each chat and attach profile data to participants
      const chatsWithLastMessage = await Promise.all(
        (data || []).map(async (chat: any) => {
          const { data: lastMessages } = await supabase
            .from('messages')
            .select('*')
            .eq('chat_id', chat.id)
            .order('created_at', { ascending: false })
            .limit(1);

          // Attach profile data to each participant
          const participantsWithUsers = chat.participants?.map((p: any) => ({
            ...p,
            user: profileMap.get(p.user_id) || null,
          })) || [];

          return {
            ...chat,
            participants: participantsWithUsers,
            last_message: lastMessages?.[0] || null,
          };
        })
      );

      // Calculate unread counts
      const chatsWithDetails: ChatWithDetails[] = await Promise.all(
        chatsWithLastMessage.map(async (chat: any) => {
          // Get user's participant record
          const userParticipant = chat.participants?.find(
            (p: any) => p.user_id === userId
          );

          // Fetch unread messages count
          let unreadCount = 0;
          if (userParticipant?.last_read_at) {
            const { count } = await supabase
              .from('messages')
              .select('*', { count: 'exact', head: true })
              .eq('chat_id', chat.id)
              .gt('created_at', userParticipant.last_read_at)
              .neq('sender_id', userId);

            unreadCount = count || 0;
          } else {
            // If never read, count all messages not from user
            const { count } = await supabase
              .from('messages')
              .select('*', { count: 'exact', head: true })
              .eq('chat_id', chat.id)
              .neq('sender_id', userId);

            unreadCount = count || 0;
          }

          return {
            ...chat,
            unread_count: unreadCount,
          };
        })
      );

      setChats(chatsWithDetails);
    } catch (err) {
      console.error('Error fetching chats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch chats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChats();

    // Subscribe to chat updates
    const channel = supabase
      .channel('chats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chats',
        },
        () => {
          fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        () => {
          fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_participants',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Refetch when user marks messages as read (last_read_at changes)
          fetchChats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  /**
   * Get chats by type
   */
  const getDirectChats = useCallback(() => chats.filter((c) => c.type === 'direct'), [chats]);
  const getGroupChats = useCallback(() => chats.filter((c) => c.type === 'group' || c.type === 'team'), [chats]);
  const getTeamChats = useCallback(() => chats.filter((c) => c.type === 'team'), [chats]);

  /**
   * Get total unread count
   */
  const getTotalUnreadCount = useCallback(() =>
    chats.reduce((sum, chat) => sum + (chat.unread_count || 0), 0), [chats]);

  /**
   * Leave/delete a chat (removes user from chat, chat disappears from list)
   */
  const leaveChat = useCallback(
    async (chatIdToLeave: string) => {
      if (!userId) return;
      try {
        const { error } = await supabase
          .from('chat_participants')
          .update({ left_at: new Date().toISOString() })
          .eq('chat_id', chatIdToLeave)
          .eq('user_id', userId);

        if (error) throw error;
        await fetchChats();
      } catch (err) {
        console.error('Error leaving chat:', err);
        throw err;
      }
    },
    [userId, fetchChats]
  );

  return {
    chats,
    loading,
    error,
    refetch: fetchChats,
    getDirectChats,
    getGroupChats,
    getTeamChats,
    getTotalUnreadCount,
    leaveChat,
  };
};

